import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { BooleanInput, required, useTranslate } from 'react-admin';
import { useFormContext, useFormState } from 'react-hook-form';
import PropTypes from 'prop-types';
import ImportFileInput from '@/components/global/inputs/ImportFileInput.jsx';
import ImportValidationDetailsDrawer from '@/components/device/ImportValidationDetailsDrawer.jsx';
import NotificationBox from '@/components/global/fields/NotificationBox.jsx';
import { pluralizeWord } from '@/helpers/format/pluralizeWord';

const ImportFormContent = ({
    showValidationDetails,
    showVerifyOnly,
    placeholderText,
    loading,
    isValidationOpen,
    validationDetails,
    resetForm,
    setIsValidationOpen,
    setLoading,
    resource,
    dataTransformer,
    importedCount,
    setImportedCount,
    open,
}) => {
    const translate = useTranslate();
    const { reset, getValues, setValue } = useFormContext();
    const { isSubmitted } = useFormState();

    const handleResetForm = () => {
        resetForm();
        reset();
    };

    const handleFileChange = () => {
        if (isSubmitted) {
            const currentValues = getValues();
            reset();
            setValue('file', currentValues.file, { shouldDirty: true });
        }
    };

    useEffect(() => {
        if (!open) {
            setImportedCount(null);
        }
    }, [open, setImportedCount]);

    useEffect(() => {
        if (importedCount !== null) {
            const timer = setTimeout(() => {
                setImportedCount(null);
            }, 6000);
            return () => clearTimeout(timer);
        }
    }, [importedCount, setImportedCount]);

    const importResult = typeof importedCount === 'number' ? { imported: importedCount, failed: 0 } : importedCount;

    return (
        <>
            <Box className={`import-box ${loading ? 'checking' : ''}`}>
                {importedCount !== null && importResult && (
                    <Box className='import-success-box'>
                        <NotificationBox variant='success' className='large'>
                            {translate('common.importSection.successInfoFor')} <strong>{importResult.imported}</strong>{' '}
                            {pluralizeWord(importResult.imported, 'common.importSection.device', translate)}.
                            {importResult.failed > 0 && (
                                <>
                                    <br></br>({importResult.failed} {pluralizeWord(importResult.failed, 'common.importSection.record', translate)}{' '}
                                    {translate('common.importSection.withErrors')}.)
                                </>
                            )}
                        </NotificationBox>
                    </Box>
                )}
                {showVerifyOnly && !showValidationDetails && (
                    <BooleanInput size='small' source='isVerifyOnly' label={translate('common.importSection.onlyVerification')} fullWidth />
                )}
                <ImportFileInput
                    source='file'
                    accept='.csv'
                    validate={required(translate('common.validation.required'))}
                    placeholderText={placeholderText}
                    loading={loading}
                    disabled={loading}
                    onFileChange={handleFileChange}
                />
            </Box>
            <ImportValidationDetailsDrawer
                open={isValidationOpen}
                onClose={() => {
                    setIsValidationOpen(false);
                    handleResetForm();
                }}
                data={validationDetails}
                resource={resource}
                dataTransformer={dataTransformer}
                setLoading={setLoading}
                loading={loading}
                onImportSuccess={count => setImportedCount(count)}
            />
        </>
    );
};

ImportFormContent.propTypes = {
    showValidationDetails: PropTypes.bool,
    showVerifyOnly: PropTypes.bool,
    placeholderText: PropTypes.string,
    loading: PropTypes.bool,
    isValidationOpen: PropTypes.bool,
    validationDetails: PropTypes.object,
    resetForm: PropTypes.func,
    setIsValidationOpen: PropTypes.func,
    setLoading: PropTypes.func,
    resource: PropTypes.string,
    dataTransformer: PropTypes.func,
    importedCount: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
            imported: PropTypes.number,
            failed: PropTypes.number,
        }),
    ]),
    setImportedCount: PropTypes.func,
    open: PropTypes.bool,
};

export default ImportFormContent;
